import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import useOnclickOutside from 'react-cool-onclickoutside';

import HeaderDropdownItem, { IHeaderDropdownItem } from './HeaderDropdownItem';

export enum HeaderDropdownPositions {
    right = 'right',
    left = 'left',
}

interface IHeaderDropdownProps {
    options: IHeaderDropdownItem[];
    dividerBeforeIds?: string[];
    children(visible: boolean): React.ReactElement;
    position?: HeaderDropdownPositions;
    disabled?: boolean;
    title?: string;
    itemClassName?: string;
    itemsListClassName?: string;
}

const HeaderDropdown: React.FC<IHeaderDropdownProps> = ({
    children,
    options,
    dividerBeforeIds,
    position,
    disabled = false,
    title,
    itemClassName,
    itemsListClassName,
}) => {
    const router = useRouter();
    const [visible, setVisible] = useState(false);
    const menu = useOnclickOutside(() => setVisible(false));

    useEffect(() => {
        const handleRouteChange = () => {
            setVisible(false);
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router.events]);

    if (!Array.isArray(options)) {
        return null;
    }

    return (
        <div ref={menu} className="relative h-full">
            <button
                type="button"
                className="relative h-full"
                onClick={() => {
                    setVisible(!visible);
                }}
                disabled={disabled}
            >
                {children(visible)}
                {visible && <span className="absolute left-0 w-full bg-red-400 h-0.5 -bottom-px" />}
            </button>
            {visible && (
                <div
                    className={clsx(
                        'absolute flex flex-col min-w-full bg-white z-60 top-20 shadow-4 rounded-b-[6px]',
                        title ? 'pb-2' : 'py-2',
                        { 'right-0': position === HeaderDropdownPositions.right },
                    )}
                >
                    {title && (
                        <div className="px-4 pt-[12px] pb-[4px]">
                            <h5 className="font-medium text-[16px] text-blueGray-800 whitespace-nowrap">
                                {title}
                            </h5>
                        </div>
                    )}
                    <div className={clsx('flex flex-col', itemsListClassName)}>
                        {options.map(option => {
                            const hasDividerBefore = dividerBeforeIds?.includes(option.id);

                            return (
                                <div key={option.id} className="w-full">
                                    {hasDividerBefore && (
                                        <div
                                            className="border-t border-blueGray-100"
                                            role="separator"
                                        />
                                    )}
                                    <HeaderDropdownItem
                                        item={option}
                                        className={clsx(itemClassName, {
                                            'mt-[10px]': hasDividerBefore,
                                        })}
                                        onSelect={() => setVisible(false)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HeaderDropdown;
